/**
 * CTAOverridesSection — gestión de overrides geo-targeted desde admin.
 *
 * Permite crear/editar/eliminar overrides de CTAs con scoping por país,
 * provincia y/o ciudad, sin necesidad de redeploy. Los cambios se
 * propagan en <1s a producción vía Cloudflare/Upstash KV (version-bump).
 *
 * Endpoints:
 *   GET    /api/admin/cta-overrides       → list
 *   POST   /api/admin/cta-overrides       → create
 *   PATCH  /api/admin/cta-overrides       → update (body.id)
 *   DELETE /api/admin/cta-overrides?id=…  → delete
 *
 * Preview en vivo:
 *   GET /api/cta-resolved?country=AR&province=Buenos%20Aires&city=Mar%20del%20Plata
 *   → muestra al operador cuáles CTAs verá un usuario en ese geo concreto.
 */

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { useAdminAuth } from "@/hooks/useAdminAuth";
// Mismo catálogo que /admin/wishlist-config, /admin/country-templates y el
// Worker. Antes este dropdown salía de `argentinaProvinces.ts`, una lista
// aparte que tenía 23 provincias: La Pampa NO figuraba, así que era imposible
// configurar un override de CTA para esa provincia. Además etiquetaba la
// capital como "Ciudad Autónoma de Buenos Aires" mientras el resto del sistema
// usa "CABA" (funcionaba por alias, pero confundía al operador).
// `scripts/validate-ar-geo-coherence.ts` vigila que no vuelva a divergir.
import { AR_CITY_CATALOG, AR_PROVINCES } from "@/config/wishlistGeoCatalog";
import { BRAND_SITE_URL } from "@/lib/brandConfig";
import {
  CheckCircle2,
  Eye,
  Gauge,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

// ─── Types (mirror del backend) ─────────────────────────────────────────────

type CtaCategory =
  | "telegram_basic"
  | "vip_membership"
  | "free_channel"
  | "special_offer"
  | "video"
  | "whatsapp_broadcast"
  | "instagram_membership"
  | "talk"
  | "group_talk";

interface CtaOverride {
  id: string;
  brand_source_key: string;
  category: CtaCategory;
  field: string;
  value: string;
  scope_country: string | null;
  scope_province: string | null;
  scope_city: string | null;
  priority: number;
  active: boolean;
  starts_at: string | null;
  expires_at: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

interface CtaKvHealth {
  brand: string;
  kv_configured: boolean;
  backend: "upstash-kv" | "postgres" | "in-memory";
  current_version: number;
  snapshot_age_seconds: number | null;
  override_count: number;
  propagation_eta_label: string;
  error?: string;
}

const CATEGORY_LABELS: Record<CtaCategory, string> = {
  telegram_basic: "Telegram Basic",
  vip_membership: "VIP Membership",
  free_channel: "Free Channel",
  special_offer: "Special Offer",
  video: "Video (Mux)",
  whatsapp_broadcast: "WhatsApp Broadcast",
  instagram_membership: "Instagram (Mejores Amigos)",
  talk: "Talk to me (1:1)",
  group_talk: "Group talk",
};

const FIELDS_BY_CATEGORY: Record<CtaCategory, { key: string; label: string; placeholder?: string }[]> = {
  telegram_basic: [
    { key: "url", label: "URL checkout Stripe (telegram)", placeholder: "https://buy.stripe.com/..." },
    {
      key: "coinbase_url",
      label: "PaymentModal — checkout Coinbase (cripto)",
      placeholder: "https://commerce.coinbase.com/checkout/...",
    },
    {
      key: "group_url",
      label: "ThankYou — grupo privado Telegram (post-pago)",
      placeholder: "https://t.me/+...",
    },
    {
      key: "broadcast_href",
      label: "BaseTemplate — enlace WhatsApp broadcast",
      placeholder: "/whatsapp-broadcast o URL absoluta",
    },
    { key: "price_cents", label: "Precio (centavos USD)", placeholder: "699" },
    { key: "price_ars", label: "Precio (ARS)", placeholder: "5000" },
    { key: "discount_pct", label: "Descuento %", placeholder: "30" },
    { key: "enabled", label: "Habilitado", placeholder: "true / false" },
    { key: "copy_es", label: "Copy ES" },
    { key: "copy_en", label: "Copy EN" },
  ],
  vip_membership: [
    { key: "url", label: "URL VIP" },
    {
      key: "menu_wishlist_url",
      label: "BaseTemplate — URL menú / wishlist",
      placeholder: `${BRAND_SITE_URL}/subscripcion`,
    },
    { key: "price_cents", label: "Precio (centavos USD)" },
    { key: "price_ars", label: "Precio (ARS)" },
    { key: "discount_pct", label: "Descuento %" },
    { key: "enabled", label: "Habilitado" },
    { key: "copy_es", label: "Copy ES" },
    { key: "copy_en", label: "Copy EN" },
    { key: "video_playback_id", label: "Mux playback ID" },
  ],
  free_channel: [
    { key: "url", label: "URL canal" },
    { key: "enabled", label: "Habilitado" },
    { key: "copy_es", label: "Copy ES" },
    { key: "copy_en", label: "Copy EN" },
  ],
  special_offer: [
    { key: "enabled", label: "Habilitado" },
    { key: "discount_pct", label: "Descuento %" },
    { key: "copy_es", label: "Copy ES" },
    { key: "copy_en", label: "Copy EN" },
    { key: "expires_at_iso", label: "Vence (ISO)" },
    { key: "banner_color", label: "Banner color (hex)" },
    {
      key: "tally_embed_src",
      label: "BaseTemplate — URL embed Tally (contacto)",
      placeholder: "https://tally.so/embed/...",
    },
  ],
  whatsapp_broadcast: [
    { key: "price_ars", label: "Precio ARS (Argentina)", placeholder: "6999" },
    { key: "price_cents", label: "Precio USD cents (internacional, Stripe)", placeholder: "699" },
    { key: "enabled", label: "Habilitado", placeholder: "true / false" },
  ],
  video: [
    {
      key: "mode",
      label: "Modo CTA (/video-vip)",
      placeholder: "sin_boton | exclusivo | comunidad | wishlist | video_vip",
    },
    { key: "playback_id", label: "Mux playback ID (video-vip)" },
    { key: "video_playback_id", label: "Mux playback ID (legacy)" },
    { key: "enabled", label: "Habilitado" },
    { key: "poster_url", label: "Poster URL" },
  ],
  instagram_membership: [
    { key: "url", label: "URL checkout Stripe", placeholder: "https://buy.stripe.com/..." },
    {
      key: "coinbase_url",
      label: "Checkout Coinbase (cripto)",
      placeholder: "https://commerce.coinbase.com/checkout/...",
    },
    { key: "price_cents", label: "Precio (centavos USD)", placeholder: "14999" },
    { key: "enabled", label: "Habilitado", placeholder: "true / false" },
  ],
  talk: [
    { key: "url", label: "URL checkout Stripe", placeholder: "https://buy.stripe.com/..." },
    {
      key: "coinbase_url",
      label: "Checkout Coinbase (cripto)",
      placeholder: "https://commerce.coinbase.com/checkout/...",
    },
    { key: "price_cents", label: "Precio (centavos USD)" },
    { key: "enabled", label: "Habilitado", placeholder: "true / false" },
  ],
  group_talk: [
    { key: "url", label: "URL checkout Stripe", placeholder: "https://buy.stripe.com/..." },
    {
      key: "coinbase_url",
      label: "Checkout Coinbase (cripto)",
      placeholder: "https://commerce.coinbase.com/checkout/...",
    },
    { key: "price_cents", label: "Precio (centavos USD)" },
    { key: "enabled", label: "Habilitado", placeholder: "true / false" },
  ],
};

const COMMON_COUNTRIES = [
  { code: "AR", name: "Argentina" },
  { code: "MX", name: "México" },
  { code: "CL", name: "Chile" },
  { code: "CO", name: "Colombia" },
  { code: "PE", name: "Perú" },
  { code: "BR", name: "Brasil" },
  { code: "US", name: "Estados Unidos" },
  { code: "ES", name: "España" },
  { code: "UY", name: "Uruguay" },
  { code: "PY", name: "Paraguay" },
];

interface FormState {
  category: CtaCategory;
  field: string;
  value: string;
  scope_country: string;
  scope_province: string;
  scope_city: string;
  active: boolean;
  expires_at: string;
  notes: string;
}

const EMPTY_FORM: FormState = {
  category: "telegram_basic",
  field: "url",
  value: "",
  scope_country: "",
  scope_province: "",
  scope_city: "",
  active: true,
  expires_at: "",
  notes: "",
};

// ─── Component ──────────────────────────────────────────────────────────────

export function CTAOverridesSection() {
  const { getAuthHeaders } = useAdminAuth();
  const { toast } = useToast();

  const [overrides, setOverrides] = useState<CtaOverride[]>([]);
  const [kvHealth, setKvHealth] = useState<CtaKvHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  // ── Preview state ────────────────────────────────────────────────────────
  const [previewCountry, setPreviewCountry] = useState("AR");
  const [previewProvince, setPreviewProvince] = useState("");
  const [previewCity, setPreviewCity] = useState("");
  const [previewData, setPreviewData] = useState<Record<string, Record<string, string>> | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // ── Fetch overrides + KV health en paralelo ──────────────────────────────
  // Ambos llamados a admin endpoints, comparten auth, mismo lifecycle de
  // refresh — los disparamos juntos con Promise.all para minimizar latencia
  // y mantener UI consistente (no mostrar overrides sin saber backend KV).
  const fetchOverrides = useCallback(async () => {
    setLoading(true);
    setError(null);
    setWarning(null);
    try {
      const headers = await getAuthHeaders();
      const [overridesRes, healthRes] = await Promise.all([
        fetch("/api/admin/cta-overrides?include_inactive=true", { headers }),
        fetch("/api/admin/cta-kv-health", { headers }),
      ]);
      const overridesJson = await overridesRes.json();
      if (!overridesRes.ok) {
        setError(overridesJson.error?.message || overridesJson.error || `HTTP ${overridesRes.status}`);
      } else {
        setOverrides(overridesJson.overrides ?? []);
        if (overridesJson.warning) setWarning(overridesJson.warning);
      }
      // KV health es best-effort: si falla, solo dejamos null y la UI muestra
      // "indeterminado" pero NO bloquea la tabla principal.
      if (healthRes.ok) {
        const healthJson = (await healthRes.json()) as CtaKvHealth;
        setKvHealth(healthJson);
      } else {
        setKvHealth(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network error");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    void fetchOverrides();
  }, [fetchOverrides]);

  // ── Preview fetch ────────────────────────────────────────────────────────
  const fetchPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const params = new URLSearchParams();
      if (previewCountry) params.set("country", previewCountry);
      if (previewProvince) params.set("province", previewProvince);
      if (previewCity) params.set("city", previewCity);
      const res = await fetch(`/api/cta-resolved?${params.toString()}`, {
        cache: "no-store",
      });
      const json = await res.json();
      setPreviewData(json.ctas ?? {});
    } catch (err) {
      toast({
        title: "Error en preview",
        description: err instanceof Error ? err.message : "Network error",
        variant: "destructive",
      });
    } finally {
      setPreviewLoading(false);
    }
  }, [previewCountry, previewProvince, previewCity, toast]);

  // ── Save (create or update) ──────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!form.value && form.field !== "enabled") {
      toast({
        title: "Falta valor",
        description: "Completá el campo Valor antes de guardar",
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    try {
      const headers = await getAuthHeaders();
      const body: Record<string, unknown> = {
        category: form.category,
        field: form.field,
        value: form.value,
        scope_country: form.scope_country || null,
        scope_province: form.scope_province || null,
        scope_city: form.scope_city || null,
        active: form.active,
        expires_at: form.expires_at || null,
        notes: form.notes || null,
      };
      if (editingId) body.id = editingId;

      const res = await fetch("/api/admin/cta-overrides", {
        method: editingId ? "PATCH" : "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok) {
        const msg = json.error?.message || json.error || `HTTP ${res.status}`;
        toast({ title: "Error", description: msg, variant: "destructive" });
        return;
      }
      toast({
        title: editingId ? "Override actualizado" : "Override creado",
        description: `Propagación KV: v${json.kv_version} (<1s a prod)`,
      });
      setEditorOpen(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      await fetchOverrides();
    } catch (err) {
      toast({
        title: "Error de red",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  }, [form, editingId, getAuthHeaders, toast, fetchOverrides]);

  // ── Delete ───────────────────────────────────────────────────────────────
  const handleDelete = useCallback(
    async (id: string) => {
      if (!confirm("¿Eliminar este override? La acción es inmediata.")) return;
      try {
        const headers = await getAuthHeaders();
        const res = await fetch(`/api/admin/cta-overrides?id=${encodeURIComponent(id)}`, {
          method: "DELETE",
          headers,
        });
        const json = await res.json();
        if (!res.ok) {
          toast({
            title: "Error",
            description: json.error?.message || json.error || `HTTP ${res.status}`,
            variant: "destructive",
          });
          return;
        }
        toast({
          title: "Override eliminado",
          description: `Cache KV invalidado: v${json.kv_version}`,
        });
        await fetchOverrides();
      } catch (err) {
        toast({
          title: "Error",
          description: err instanceof Error ? err.message : String(err),
          variant: "destructive",
        });
      }
    },
    [getAuthHeaders, toast, fetchOverrides],
  );

  // ── Edit (load form from row) ────────────────────────────────────────────
  const handleEdit = useCallback((row: CtaOverride) => {
    setEditingId(row.id);
    setForm({
      category: row.category,
      field: row.field,
      value: row.value,
      scope_country: row.scope_country ?? "",
      scope_province: row.scope_province ?? "",
      scope_city: row.scope_city ?? "",
      active: row.active,
      expires_at: row.expires_at ? row.expires_at.slice(0, 16) : "",
      notes: row.notes ?? "",
    });
    setEditorOpen(true);
  }, []);

  // Available fields for current category — incluye campo actual si vino de la DB y no está en la lista (edición segura)
  const availableFields = useMemo(() => {
    const base = FIELDS_BY_CATEGORY[form.category];
    if (base.some((f) => f.key === form.field)) return base;
    return [...base, { key: form.field, label: `${form.field} (personalizado)` }];
  }, [form.category, form.field]);

  // Group by category
  const grouped = useMemo(() => {
    const out: Record<string, CtaOverride[]> = {};
    for (const o of overrides) {
      if (!out[o.category]) out[o.category] = [];
      out[o.category].push(o);
    }
    return out;
  }, [overrides]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="text-lg">Overrides geo-targeted</CardTitle>
            <CardDescription>
              Cambios dinámicos sin redeploy — propagación &lt;1s vía KV.
              Scoping: país / provincia / ciudad.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void fetchOverrides()}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              )}
              Refrescar
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setEditingId(null);
                setForm(EMPTY_FORM);
                setEditorOpen(true);
              }}
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Nuevo override
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* ── KV health banner — 3 tiers ─────────────────────────────────── */}
        {/* Verde si Upstash KV (tier 1) — sub-segundo + escala alta.        */}
        {/* Azul  si Postgres KV (tier 2) — sub-segundo + zero deps extra.   */}
        {/* Ámbar si in-memory (tier 3) — propagación lenta entre regiones.  */}
        {kvHealth && (() => {
          const isFast = kvHealth.backend === "upstash-kv" || kvHealth.backend === "postgres";
          const tierColors = {
            "upstash-kv": "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
            "postgres":   "bg-sky-500/10 border-sky-500/30 text-sky-300",
            "in-memory":  "bg-amber-500/10 border-amber-500/30 text-amber-300",
          } as const;
          const tierLabels = {
            "upstash-kv": "Upstash Redis REST ✓",
            "postgres":   "Postgres-backed kv_store ✓",
            "in-memory":  "in-memory (fallback)",
          } as const;
          return (
            <div
              className={
                "text-xs flex items-start gap-2 p-3 rounded border " +
                tierColors[kvHealth.backend]
              }
            >
              {isFast ? (
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              ) : (
                <TriangleAlert className="w-4 h-4 shrink-0 mt-0.5" />
              )}
              <div className="space-y-1 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">
                    Cache backend: {tierLabels[kvHealth.backend]}
                  </span>
                  <span className="text-muted-foreground/80">·</span>
                  <span className="flex items-center gap-1">
                    <Gauge className="w-3 h-3" />
                    {kvHealth.propagation_eta_label}
                  </span>
                </div>
                <div className="font-mono text-[10px] flex flex-wrap gap-x-3 gap-y-0.5 opacity-80">
                  <span>v{kvHealth.current_version}</span>
                  <span>·</span>
                  <span>{kvHealth.override_count} overrides activos</span>
                  {kvHealth.snapshot_age_seconds != null && (
                    <>
                      <span>·</span>
                      <span>
                        snapshot age:{" "}
                        {kvHealth.snapshot_age_seconds < 60
                          ? `${kvHealth.snapshot_age_seconds}s`
                          : `${Math.round(kvHealth.snapshot_age_seconds / 60)}m`}
                      </span>
                    </>
                  )}
                </div>
                {kvHealth.backend === "postgres" && (
                  <div className="text-[10px] opacity-90">
                    💡 Usando tabla <code>kv_store</code> sobre tu Postgres existente.
                    Para upgrade a Upstash Redis REST (escala mayor): configurá{" "}
                    <code>KV_REST_API_URL</code> y <code>KV_REST_API_TOKEN</code> en Railway
                    y redeploy.
                  </div>
                )}
                {kvHealth.backend === "in-memory" && (
                  <div className="text-[10px] opacity-90">
                    ⚙️ DB no disponible y Upstash no configurado. Para activar
                    propagación &lt;1s: ejecutar migración <code>073_kv_store</code> o
                    configurar Upstash Redis REST en Railway.
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {warning && (
          <div className="text-xs text-amber-500 bg-amber-500/10 p-3 rounded border border-amber-500/30">
            ⚠️ {warning}
          </div>
        )}
        {error && (
          <div className="text-xs text-red-400 bg-red-500/10 p-3 rounded border border-red-500/30">
            Error: {error}
          </div>
        )}

        {!loading && overrides.length === 0 && !warning && (
          <div className="text-center py-8 text-sm text-muted-foreground">
            No hay overrides activos. Click en{" "}
            <strong>Nuevo override</strong> para crear el primero.
          </div>
        )}

        {/* Tabla agrupada por categoría */}
        {Object.entries(grouped).map(([category, rows]) => (
          <div key={category} className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {CATEGORY_LABELS[category as CtaCategory] ?? category} ({rows.length})
            </h3>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[140px]">Field</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead className="w-[180px]">Scope geo</TableHead>
                    <TableHead className="w-[80px] text-center">Prio</TableHead>
                    <TableHead className="w-[80px] text-center">Activo</TableHead>
                    <TableHead className="w-[140px]">Expira</TableHead>
                    <TableHead className="w-[110px] text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id} className={!row.active ? "opacity-60" : ""}>
                      <TableCell className="font-mono text-xs">{row.field}</TableCell>
                      <TableCell className="font-mono text-xs max-w-[220px] truncate" title={row.value}>
                        {row.value}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {row.scope_country && (
                            <Badge variant="outline" className="text-[10px]">
                              🌐 {row.scope_country}
                            </Badge>
                          )}
                          {row.scope_province && (
                            <Badge variant="outline" className="text-[10px]">
                              📍 {row.scope_province}
                            </Badge>
                          )}
                          {row.scope_city && (
                            <Badge variant="outline" className="text-[10px]">
                              🏙 {row.scope_city}
                            </Badge>
                          )}
                          {!row.scope_country && !row.scope_province && !row.scope_city && (
                            <Badge variant="secondary" className="text-[10px]">
                              global
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center text-xs tabular-nums">
                        {row.priority}
                      </TableCell>
                      <TableCell className="text-center">
                        {row.active ? (
                          <Badge className="text-[10px] bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20">
                            ON
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px]">
                            OFF
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {row.expires_at
                          ? new Date(row.expires_at).toLocaleDateString()
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(row)}
                            className="h-7 w-7 p-0"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => void handleDelete(row.id)}
                            className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ))}

        {/* ── Preview por geo ───────────────────────────────────────────── */}
        <Card className="bg-muted/20 border-dashed">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Preview por geo
            </CardTitle>
            <CardDescription className="text-xs">
              Simulá qué CTAs verá un usuario en una ubicación específica.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <Label className="text-[10px]">País (ISO-2)</Label>
                <Input
                  value={previewCountry}
                  onChange={(e) => setPreviewCountry(e.target.value.toUpperCase().slice(0, 2))}
                  placeholder="AR"
                  className="h-8 text-xs"
                />
              </div>
              <div>
                <Label className="text-[10px]">Provincia</Label>
                <Input
                  value={previewProvince}
                  onChange={(e) => setPreviewProvince(e.target.value)}
                  placeholder="Buenos Aires"
                  className="h-8 text-xs"
                />
              </div>
              <div>
                <Label className="text-[10px]">Ciudad</Label>
                <Input
                  value={previewCity}
                  onChange={(e) => setPreviewCity(e.target.value)}
                  placeholder="Mar del Plata"
                  className="h-8 text-xs"
                />
              </div>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void fetchPreview()}
              disabled={previewLoading}
            >
              {previewLoading ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <Eye className="w-3.5 h-3.5 mr-1.5" />
              )}
              Resolver para este geo
            </Button>
            {previewData && (
              <div className="text-xs space-y-2">
                {Object.keys(previewData).length === 0 ? (
                  <div className="text-muted-foreground italic">
                    No hay overrides aplicables — usuario verá los defaults
                    (env vars).
                  </div>
                ) : (
                  Object.entries(previewData).map(([cat, fields]) => (
                    <div key={cat} className="bg-background p-2 rounded border">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                        {CATEGORY_LABELS[cat as CtaCategory] ?? cat}
                      </div>
                      <div className="font-mono">
                        {Object.entries(fields).map(([k, v]) => (
                          <div key={k}>
                            <span className="text-muted-foreground">{k}:</span>{" "}
                            <span>{v}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </CardContent>

      {/* ── Editor dialog ─────────────────────────────────────────────────── */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Editar override" : "Nuevo override"}
            </DialogTitle>
            <DialogDescription>
              Cambios propagados a producción en menos de 1s vía KV.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Categoría</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) =>
                    setForm({
                      ...form,
                      category: v as CtaCategory,
                      field: FIELDS_BY_CATEGORY[v as CtaCategory][0].key,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_LABELS).map(([k, label]) => (
                      <SelectItem key={k} value={k}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Campo</Label>
                <Select
                  value={form.field}
                  onValueChange={(v) => setForm({ ...form, field: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableFields.map((f) => (
                      <SelectItem key={f.key} value={f.key}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Valor</Label>
              <Input
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                placeholder={availableFields.find((f) => f.key === form.field)?.placeholder}
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Booleanos: <code>true</code>/<code>false</code>. Precios en
                centavos USD. ARS en pesos enteros.
              </p>
            </div>

            <div className="border-t pt-3 space-y-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Scope geo (opcional)
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>País</Label>
                  <Select
                    value={form.scope_country || "_all_"}
                    onValueChange={(v) =>
                      setForm({ ...form, scope_country: v === "_all_" ? "" : v })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all_">Todos</SelectItem>
                      {COMMON_COUNTRIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.code} — {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Provincia</Label>
                  {form.scope_country === "AR" ? (
                    <Select
                      value={form.scope_province || "_all_"}
                      onValueChange={(v) =>
                        setForm({ ...form, scope_province: v === "_all_" ? "" : v })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_all_">Todas</SelectItem>
                        {AR_PROVINCES.map((p) => (
                          <SelectItem key={p.code} value={p.name}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={form.scope_province}
                      onChange={(e) =>
                        setForm({ ...form, scope_province: e.target.value })
                      }
                      placeholder="(opcional)"
                    />
                  )}
                </div>
                <div>
                  <Label>Ciudad</Label>
                  {/* Texto libre (CF puede reportar ciudades fuera del catálogo),
                      pero con autocompletado del catálogo AR: un typo acá
                      guardaba un override que no matcheaba nunca y no había
                      forma de darse cuenta desde la UI. */}
                  <Input
                    value={form.scope_city}
                    onChange={(e) => setForm({ ...form, scope_city: e.target.value })}
                    placeholder="(opcional)"
                    list={form.scope_country === "AR" ? "ar-city-catalog" : undefined}
                  />
                  {form.scope_country === "AR" && (
                    <datalist id="ar-city-catalog">
                      {AR_CITY_CATALOG.map((c) => (
                        <option key={c.geo_code} value={c.name ?? c.geo_code} />
                      ))}
                    </datalist>
                  )}
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground">
                Más específico gana. Ciudad &gt; Provincia &gt; País &gt; Global.
              </p>
            </div>

            <div className="border-t pt-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Vence (opcional)</Label>
                  <Input
                    type="datetime-local"
                    value={form.expires_at}
                    onChange={(e) =>
                      setForm({ ...form, expires_at: e.target.value })
                    }
                  />
                </div>
                <div className="flex items-end gap-2 pb-1">
                  <Switch
                    id="active-switch"
                    checked={form.active}
                    onCheckedChange={(v) => setForm({ ...form, active: v })}
                  />
                  <Label htmlFor="active-switch">Activo</Label>
                </div>
              </div>
              <div>
                <Label>Notas internas</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Por qué se creó este override..."
                  rows={2}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditorOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button onClick={() => void handleSave()} disabled={submitting}>
              {submitting && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              {editingId ? "Actualizar" : "Crear override"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
